/* eslint-disable jsx-a11y/no-onchange */
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import React from 'react';
import { useActiveProjectId, useFeatures, usePolicy } from '../../../../hooks';
import { enforce } from '../../../../util/abac';
import { RegenerateCoachingModal } from './RegenerateCoachingModal';
import { usePulseCoachRegeneration } from './usePulseCoachRegeneration';
import {
  getZoriCoachSourceRunId,
  useZoriCoachCarePlans,
  ZoriCoachCarePlan,
} from './useZoriCoachCarePlans';
import { ZoriCoachingActionItem } from './ZoriCoachingActionItem';
import { ZoriCoachingNarrative } from './ZoriCoachingNarrative';

const ZORI_PULSE_SCAN_AGENT = 'api-zori-pulse-scan';

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: theme.palette.background.default,
    minHeight: 0,
    overflowY: 'auto',
    padding: theme.spacing(2),
  },
  toolbar: {
    alignItems: 'flex-end',
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  selectorGroup: {
    display: 'flex',
    flex: '1 1 20rem',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  select: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.pxToRem(4),
    color: theme.palette.text.primary,
    font: 'inherit',
    minHeight: theme.pxToRem(44),
    padding: theme.spacing(1),
    width: '100%',
  },
  metadata: {
    color: theme.palette.text.hint,
  },
  actions: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  centeredState: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    justifyContent: 'center',
    minHeight: theme.pxToRem(240),
    textAlign: 'center',
  },
  inlineState: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
}));

const formatInstant = (value?: string): string | undefined => {
  const timestamp = Date.parse(value ?? '');
  return Number.isNaN(timestamp)
    ? undefined
    : new Date(timestamp).toLocaleString();
};

const formatSnapshotDate = (carePlan: ZoriCoachCarePlan): string =>
  formatInstant(carePlan.period?.start) ??
  `Date unavailable — ${carePlan.id.slice(0, 8)}`;

export const ZoriCoachingWorkspace: React.FC<{ patientId: string }> = ({
  patientId,
}) => {
  const classes = useStyles();
  const projectId = useActiveProjectId();
  const hasFeature = useFeatures();
  const policy = usePolicy();
  const {
    carePlans,
    loading,
    initialError,
    historyError,
    refetch,
    loadOlder,
    hasOlder,
    isLoadingOlder,
  } = useZoriCoachCarePlans(patientId);
  const [selectedCarePlanId, setSelectedCarePlanId] = React.useState<
    string | undefined
  >();
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  React.useEffect(() => {
    setSelectedCarePlanId(undefined);
  }, [patientId, projectId]);

  const canInvokeZoriPulseScan =
    enforce('invokeAgent', policy as any, {
      agent: ZORI_PULSE_SCAN_AGENT,
    }) || enforce('accessAdmin', policy as any);
  const canRegenerate = hasFeature('zoriPulseScan') && canInvokeZoriPulseScan;

  const handleRegenerationCompleted = React.useCallback(async () => {
    const didRefetch = await refetch();
    if (!didRefetch) {
      throw new Error('Coaching snapshots could not be refreshed');
    }
    setSelectedCarePlanId(undefined);
  }, [refetch]);

  const { triggerRegenerate, isRegenerating } = usePulseCoachRegeneration({
    patientId,
    projectId,
    enabled: canRegenerate,
    onCompleted: handleRegenerationCompleted,
  });

  if (loading && carePlans.length === 0) {
    return (
      <div className={classes.centeredState} role="status" aria-live="polite">
        <DotLoader size={0} />
        <Text>Loading coaching snapshots…</Text>
      </div>
    );
  }

  if (initialError) {
    return (
      <div className={classes.centeredState} role="alert">
        <Text>We could not load coaching snapshots.</Text>
        <Button onClick={() => void refetch()}>Retry</Button>
      </div>
    );
  }

  const selectedCarePlan =
    carePlans.find(carePlan => carePlan.id === selectedCarePlanId) ??
    carePlans[0];
  const narrative = selectedCarePlan?.description?.trim();
  const actionActivities = selectedCarePlan?.activity ?? [];
  const sourceRunId = getZoriCoachSourceRunId(selectedCarePlan);
  const periodStart = selectedCarePlan?.period?.start;
  const periodEnd = selectedCarePlan?.period?.end;
  const formattedStart = formatInstant(periodStart);
  const formattedEnd = formatInstant(periodEnd);
  const regenerateModal = (
    <RegenerateCoachingModal
      isOpen={isModalOpen}
      onDismiss={() => setIsModalOpen(false)}
      onConfirm={() => void triggerRegenerate()}
      disabled={isRegenerating}
    />
  );

  if (!selectedCarePlan) {
    return (
      <>
        <div className={classes.centeredState}>
          <Text role="heading" aria-level={2}>
            No coaching snapshots
          </Text>
          <Text>Generate coaching to create the first snapshot.</Text>
          {canRegenerate ? (
            <Button
              onClick={() => setIsModalOpen(true)}
              disabled={isRegenerating}
            >
              Regenerate coaching
            </Button>
          ) : null}
        </div>
        {regenerateModal}
      </>
    );
  }

  return (
    <Box className={classes.root} direction="column" gap={2} fullHeight>
      <Text role="heading" aria-level={1}>
        Zori Coaching
      </Text>
      <div className={classes.toolbar}>
        <div className={classes.selectorGroup}>
          <label htmlFor="zori-coaching-snapshot">Coaching snapshot</label>
          <select
            id="zori-coaching-snapshot"
            className={classes.select}
            value={selectedCarePlan.id}
            onChange={event => setSelectedCarePlanId(event.target.value)}
          >
            {carePlans.map(carePlan => (
              <option key={carePlan.id} value={carePlan.id}>
                {formatSnapshotDate(carePlan)}
              </option>
            ))}
          </select>
        </div>
        {canRegenerate ? (
          <Button
            onClick={() => setIsModalOpen(true)}
            disabled={isRegenerating}
          >
            Regenerate coaching
          </Button>
        ) : null}
      </div>
      <div key={selectedCarePlan.id}>
        <Box direction="column" gap={2}>
          {formattedStart && formattedEnd && periodStart && periodEnd ? (
            <Text className={classes.metadata}>
              Weekly window <time dateTime={periodStart}>{formattedStart}</time>
              {' – '}
              <time dateTime={periodEnd}>{formattedEnd}</time>
            </Text>
          ) : (
            <Text className={classes.metadata}>
              Generated{' '}
              {periodStart && formattedStart ? (
                <time dateTime={periodStart}>{formattedStart}</time>
              ) : (
                formatSnapshotDate(selectedCarePlan)
              )}
            </Text>
          )}
          {narrative ? (
            <ZoriCoachingNarrative
              carePlanId={selectedCarePlan.id}
              narrative={narrative}
              subjectId={patientId}
              sourceRunId={sourceRunId}
            />
          ) : null}
          {actionActivities.length ? (
            <div
              className={classes.actions}
              role="group"
              aria-label="Coaching actions"
            >
              {actionActivities.map((activity, index) => (
                <ZoriCoachingActionItem
                  key={activity.id ?? index}
                  carePlanId={selectedCarePlan.id}
                  activity={activity}
                  subjectId={patientId}
                  sourceRunId={sourceRunId}
                />
              ))}
            </div>
          ) : narrative ? (
            <Text role="status">
              No weekly coaching tasks in this snapshot.
            </Text>
          ) : (
            <Text>No coaching content is available in this snapshot.</Text>
          )}
        </Box>
      </div>
      {isLoadingOlder ? (
        <div className={classes.inlineState} role="status" aria-live="polite">
          <DotLoader size={0} />
          <Text>Loading older snapshots…</Text>
        </div>
      ) : historyError ? (
        <div className={classes.inlineState} role="alert">
          <Text>We could not load older snapshots.</Text>
          <Button onClick={() => void loadOlder()}>Retry older</Button>
        </div>
      ) : hasOlder ? (
        <Button onClick={() => void loadOlder()}>Load older snapshots</Button>
      ) : null}
      {regenerateModal}
    </Box>
  );
};

export default ZoriCoachingWorkspace;
